import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';

import { CodeBlockWithCopy } from '../components/CodeBlockWithCopy';
import { SEO, SITE_URL, breadcrumbJsonLd } from '../components/SEO';
import { getApiDocMarkdown } from '../content/apiDocs';
import { extractMarkdownToc, normalizeHeadingText, type TocItem } from '../utils/markdownToc';

function getTextFromChildren(children: unknown): string {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(getTextFromChildren).join('');
  if (typeof children === 'object' && 'props' in (children as any)) {
    return getTextFromChildren((children as any).props?.children);
  }
  return '';
}

function scrollToHash() {
  const id = window.location.hash.replace(/^#/, '');
  if (!id) return;
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
}

export default function ApiDocs() {
  const { i18n, t } = useTranslation();
  const markdown = useMemo(
    () => getApiDocMarkdown(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );
  const toc = useMemo(() => extractMarkdownToc(markdown, [2, 3]), [markdown]);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>('');

  const tocByLine = useMemo(() => {
    const map = new Map<number, TocItem>();
    for (const item of toc) {
      map.set(item.line, item);
    }
    return map;
  }, [toc]);

  useEffect(() => {
    scrollToHash();
    const onHashChange = () => scrollToHash();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Track active section for TOC highlighting
  useEffect(() => {
    // Use a small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      const tocIds = new Set(toc.map((item) => item.id));

      const handleScroll = () => {
        const headings = Array.from(document.querySelectorAll('h2[id], h3[id]')) as HTMLElement[];
        if (headings.length === 0) return;

        const scrollY = window.scrollY;
        const offset = 120;

        // Find the last heading that has scrolled past the offset
        let currentId = '';
        for (const heading of headings) {
          const top = heading.offsetTop - offset;
          if (scrollY >= top && tocIds.has(heading.id)) {
            currentId = heading.id;
          }
        }

        // Default to first TOC item if at top
        if (!currentId && toc.length > 0) {
          currentId = toc[0].id;
        }

        setActiveId(currentId);
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => window.removeEventListener('scroll', handleScroll);
    }, 100);

    return () => clearTimeout(timer);
  }, [toc]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-zinc-950 dark:to-zinc-900">
      <SEO
        title="Transcription API Documentation | OmniScribe"
        description="Integrate OmniScribe audio and video transcription with upload, transcription, export, sharing, and account API endpoints."
        path="/api"
        keywords={[
          'transcription API',
          'audio to text API',
          'video transcription API',
          'speech to text API',
          'AI transcription developer API',
        ]}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'API Documentation', path: '/api' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: 'OmniScribe API Documentation',
            description: 'Developer documentation for integrating OmniScribe transcription APIs.',
            url: `${SITE_URL}/api`,
            publisher: {
              '@type': 'Organization',
              name: 'OmniScribe',
            },
          },
        ]}
      />
      <div className="container mx-auto px-4 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-end mb-8">
            <button
              type="button"
              onClick={() => setMobileTocOpen((v) => !v)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-zinc-700 text-sm font-medium text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              {t('apiDocs.toc')}
            </button>
          </div>

          {/* Mobile TOC */}
          {mobileTocOpen && (
            <div className="lg:hidden mb-6 p-4 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-lg">
              <TocList toc={toc} activeId={activeId} onNavigate={() => setMobileTocOpen(false)} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-8 lg:gap-12">
            {/* Main Content */}
            <article className="min-w-0 prose-container">
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800 shadow-sm overflow-hidden">
                <div className="px-6 py-8 sm:px-8 lg:px-10">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: (props) => (
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-4 mt-0 pb-4 border-b border-slate-200 dark:border-zinc-800" {...props} />
                      ),
                      h2: ({ children, ...props }) => {
                        const text = normalizeHeadingText(getTextFromChildren(children));
                        const line = (props as any).node?.position?.start?.line as number | undefined;
                        const id = (line ? tocByLine.get(line)?.id : undefined) ?? text;
                        return (
                          <h2
                            id={id}
                            className="group scroll-mt-24 text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-4 mt-12 first:mt-0 pb-2 border-b border-slate-100 dark:border-zinc-800"
                            {...props}
                          >
                            <a href={`#${id}`} className="flex items-center gap-2 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              {children}
                              <svg className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </a>
                          </h2>
                        );
                      },
                      h3: ({ children, ...props }) => {
                        const text = normalizeHeadingText(getTextFromChildren(children));
                        const line = (props as any).node?.position?.start?.line as number | undefined;
                        const id = (line ? tocByLine.get(line)?.id : undefined) ?? text;
                        return (
                          <h3
                            id={id}
                            className="group scroll-mt-24 text-base font-semibold text-slate-800 dark:text-zinc-100 mb-3 mt-8 first:mt-0"
                            {...props}
                          >
                            <a href={`#${id}`} className="flex items-center gap-2 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              {children}
                              <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-50 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </a>
                          </h3>
                        );
                      },
                      p: (props) => (
                        <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed mb-4 last:mb-0" {...props} />
                      ),
                      ul: (props) => (
                        <ul className="list-disc pl-5 space-y-2 mb-4 last:mb-0 text-sm text-slate-600 dark:text-zinc-400" {...props} />
                      ),
                      ol: (props) => (
                        <ol className="list-decimal pl-5 space-y-2 mb-4 last:mb-0 text-sm text-slate-600 dark:text-zinc-400" {...props} />
                      ),
                      li: (props) => <li className="leading-relaxed" {...props} />,
                      strong: (props) => <strong className="font-semibold text-slate-800 dark:text-zinc-200" {...props} />,
                      em: (props) => <em className="italic text-slate-600 dark:text-zinc-400" {...props} />,
                      blockquote: (props) => (
                        <blockquote className="border-l-4 border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-950/30 rounded-r-lg pl-4 pr-4 py-3 text-sm text-amber-800 dark:text-amber-200 mb-4 [&>p]:mb-0" {...props} />
                      ),
                      hr: () => (
                        <hr className="my-8 border-slate-200 dark:border-zinc-800" />
                      ),
                      table: (props) => (
                        <div className="overflow-x-auto mb-4 rounded-lg border border-slate-200 dark:border-zinc-700">
                          <table className="min-w-full divide-y divide-slate-200 dark:divide-zinc-700" {...props} />
                        </div>
                      ),
                      thead: (props) => (
                        <thead className="bg-slate-50 dark:bg-zinc-800/50" {...props} />
                      ),
                      th: (props) => (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-zinc-300 uppercase tracking-wider" {...props} />
                      ),
                      td: (props) => (
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-zinc-400 border-t border-slate-100 dark:border-zinc-800" {...props} />
                      ),
                      code: ({ inline, className, children, ...props }: any) =>
                        inline ? (
                          <code
                            className="rounded-md bg-slate-100 dark:bg-zinc-800 px-1.5 py-0.5 text-[0.85em] font-mono text-indigo-600 dark:text-indigo-400 border border-slate-200 dark:border-zinc-700"
                            {...props}
                          >
                            {children}
                          </code>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        ),
                      pre: ({ children }) => {
                        const childArray = Array.isArray(children) ? children : [children];
                        const codeEl = childArray.find(Boolean) as any;
                        const code = typeof codeEl?.props?.children === 'string'
                          ? codeEl.props.children
                          : Array.isArray(codeEl?.props?.children)
                            ? codeEl.props.children.join('')
                            : '';
                        const className = String(codeEl?.props?.className || '');
                        const language = className.startsWith('language-') ? className.replace('language-', '') : undefined;
                        return <CodeBlockWithCopy code={code} language={language} />;
                      },
                      a: (props) => (
                        <a
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline underline-offset-2 decoration-indigo-300 dark:decoration-indigo-700 hover:decoration-indigo-500 transition-colors"
                          target="_blank"
                          rel="noreferrer"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {markdown}
                  </ReactMarkdown>
                </div>
              </div>
            </article>

            {/* Sidebar TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="p-5 rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white mb-4 pb-3 border-b border-slate-100 dark:border-zinc-800">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                    {t('apiDocs.tocNavigation')}
                  </div>
                  <TocList toc={toc} activeId={activeId} />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

function TocList({ toc, activeId, onNavigate }: { toc: TocItem[]; activeId?: string; onNavigate?: () => void }) {
  return (
    <nav className="space-y-0.5 max-h-[calc(100vh-200px)] overflow-y-auto">
      {toc.map((item) => {
        const isActive = activeId === item.id;
        return (
          <a
            key={`${item.id}-${item.level}`}
            href={`#${item.id}`}
            onClick={onNavigate}
            className={[
              'block py-1.5 text-[13px] transition-colors duration-150 border-l-2',
              item.level === 3 ? 'pl-5' : 'pl-3',
              isActive
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 font-medium'
                : 'border-transparent text-slate-500 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300 hover:border-slate-300 dark:hover:border-zinc-600',
            ].join(' ')}
          >
            {item.text}
          </a>
        );
      })}
    </nav>
  );
}
